import React, { useState } from 'react'
import {
  Box,
  Paper,
  TextField,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SoapTemplateItem from '@/apps/legacy/components/molecules/SoapTemplateItem'
import { ActionButton } from '@/apps/legacy/components/portal/CustomButton'
import KeywordInput from '@/components/molecules/KeywordInput'
import KeywordSuggestionModal from '@/components/molecules/KeywordSuggestionModal'
import {
  SoapTemplateGroup,
  SoapType,
  KeywordSuggestionResponse,
} from '@/features/internal/drugs/types/soapTemplate'
import { SECONDARY_ICON_BUTTON_STYLE } from '@/shared/constants/buttonStyles'

interface SoapTemplateSectionProps {
  /**
   * SOAPテンプレートグループデータ
   */
  group: SoapTemplateGroup

  /**
   * 編集モードかどうか
   */
  editMode?: boolean

  /**
   * 医薬品グループ名（キーワード提案時のコンテキスト用）
   */
  drugGroupName?: string

  /**
   * グループ名変更時に呼び出されるコールバック関数
   */
  onGroupNameChange: (groupId: string, name: string) => void

  /**
   * キーワード変更時に呼び出されるコールバック関数
   */
  onKeywordsChange: (groupId: string, keywords: string[]) => void

  /**
   * 新しいSOAP項目追加時に呼び出されるコールバック関数
   */
  onAddItem: (groupId: string, soapType: SoapType) => void

  /**
   * SOAP項目削除時に呼び出されるコールバック関数
   */
  onDeleteItem: (groupId: string, itemId: string) => void

  /**
   * SOAP項目内容変更時に呼び出されるコールバック関数
   */
  onItemContentChange: (
    groupId: string,
    itemId: string,
    content: string
  ) => void

  /**
   * グループ削除時に呼び出されるコールバック関数
   */
  onDeleteGroup: (groupId: string) => void

  /**
   * キーワード提案API呼び出し関数（オプション）
   */
  onSuggestKeywords?: (
    groupId: string,
    templateContent: string,
    existingKeywords: string[]
  ) => Promise<KeywordSuggestionResponse>
}

// スタイル付きコンポーネント
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}))

/**
 * SOAPテンプレートグループとその中の項目を管理するコンポーネント
 */
const SoapTemplateSection: React.FC<SoapTemplateSectionProps> = ({
  group,
  editMode = false,
  drugGroupName,
  onGroupNameChange,
  onKeywordsChange,
  onAddItem,
  onDeleteItem,
  onItemContentChange,
  onDeleteGroup,
  onSuggestKeywords,
}) => {
  // SOAPタイプ選択メニュー用の状態
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // KeywordSuggestionModalが期待する結果の型定義
  interface KeywordSuggestionResult {
    analysisContent: string[]
    suggestedKeywords: string[]
  }

  // キーワード提案モーダル用の状態
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false)
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false)
  const [suggestionResult, setSuggestionResult] =
    useState<KeywordSuggestionResult | null>(null)

  // メニュー開閉ハンドラー
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  // SOAP項目追加ハンドラー
  const handleAddItem = (soapType: SoapType) => {
    onAddItem(group.id, soapType)
    handleCloseMenu()
  }

  // キーワード提案ハンドラー
  const handleSuggestKeywords = async () => {
    if (!onSuggestKeywords) return

    const templateContent = group.templates
      .map((template) => `${template.soap_type}: ${template.content}`)
      .join('\n')

    if (!templateContent.trim()) {
      return
    }

    setIsLoadingSuggestion(true)
    setSuggestionModalOpen(true)

    try {
      const result = await onSuggestKeywords(
        group.id,
        templateContent,
        group.keywords
      )
      // KeywordSuggestionResponseからdataを抽出してセット
      if (result.success && result.data) {
        setSuggestionResult(result.data)
      } else {
        setSuggestionResult(null)
      }
    } catch (_error) {
      setSuggestionResult(null)
    } finally {
      setIsLoadingSuggestion(false)
    }
  }

  // 提案されたキーワードを適用
  const handleApplySuggestedKeywords = (suggestedKeywords: string[]) => {
    const newKeywords = [...group.keywords, ...suggestedKeywords]
    onKeywordsChange(group.id, newKeywords)
  }

  // キーワード提案モーダルを閉じる
  const handleCloseSuggestionModal = () => {
    setSuggestionModalOpen(false)
    setSuggestionResult(null)
    setIsLoadingSuggestion(false)
  }

  // 選択できるSOAP種別のリスト
  const soapTypes: { type: SoapType; label: string }[] = [
    { type: 'S', label: 'Subjective（主観的情報）' },
    { type: 'O', label: 'Objective（客観的情報）' },
    { type: 'A', label: 'Assessment（評価）' },
    { type: 'OP', label: 'Observation Plan（観察計画）' },
    { type: 'EP', label: 'Education Plan（教育計画）' },
    { type: 'CP', label: 'Care Plan（ケア計画）' },
  ]

  return (
    <StyledPaper>
      {editMode && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 1,
          }}
        >
          <Tooltip title="削除" placement="top">
            <IconButton
              size="small"
              onClick={() => {
                if (window.confirm(`「${group.name}」を削除しますか？`)) {
                  onDeleteGroup(group.id)
                }
              }}
              sx={SECONDARY_ICON_BUTTON_STYLE}
              aria-label="グループを削除"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <SectionHeader>
        {editMode ? (
          <TextField
            variant="outlined"
            size="small"
            value={group.name}
            onChange={(e) => onGroupNameChange(group.id, e.target.value)}
            placeholder="グループ名を入力"
            sx={{ flexGrow: 1 }}
            aria-label="SOAPテンプレートグループ名"
          />
        ) : (
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {group.name}
          </Typography>
        )}
      </SectionHeader>
      {editMode && (
        <Box sx={{ mb: 3 }}>
          <KeywordInput
            keywords={group.keywords}
            onChange={(keywords) => onKeywordsChange(group.id, keywords)}
            placeholder="キーワードを入力してEnterまたはカンマで追加"
            showSuggestionButton={!!onSuggestKeywords}
            onSuggestKeywords={handleSuggestKeywords}
            suggestionButtonDisabled={isLoadingSuggestion}
          />
        </Box>
      )}
      {!editMode && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            キーワード:
          </Typography>
          {group.keywords.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {group.keywords.map((keyword, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  {keyword}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              キーワードが登録されていません
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {group.templates.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          {group.templates.map((item) => (
            <SoapTemplateItem
              key={item.id}
              item={item}
              onContentChange={(content) =>
                onItemContentChange(group.id, item.id!, content)
              }
              onDelete={() => onDeleteItem(group.id, item.id!)}
              editMode={editMode}
            />
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {editMode
            ? 'まだSOAP項目がありません。「新しいSOAP項目を追加」ボタンをクリックして項目を追加してください。'
            : 'まだSOAP項目がありません。'}
        </Typography>
      )}

      {editMode && (
        <>
          <ActionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenMenu}
            sx={{ mt: 1, alignSelf: 'flex-start' }}
          >
            新しいSOAP項目を追加
          </ActionButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
          >
            {soapTypes.map((soap) => (
              <MenuItem
                key={soap.type}
                onClick={() => handleAddItem(soap.type)}
              >
                <Typography variant="body2">
                  <strong>{soap.type}</strong>: {soap.label}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* キーワード提案モーダル */}
      <KeywordSuggestionModal
        open={suggestionModalOpen}
        onClose={handleCloseSuggestionModal}
        onApplyKeywords={handleApplySuggestedKeywords}
        templateContent={group.templates
          .map((template) => `${template.soap_type}: ${template.content}`)
          .join('\n')}
        drugGroupName={drugGroupName}
        existingKeywords={group.keywords}
        isLoading={isLoadingSuggestion}
        suggestionResult={suggestionResult}
      />
    </StyledPaper>
  )
}

export default SoapTemplateSection
